import { IAbsenceGroup } from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroups.models";
import { PresenceType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { IEmployeeDetails } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { ErrorCode } from "src/utils/Company/Absences/getImportErrorMessage";
import { SkippedRowReason } from "src/utils/Company/Absences/getImportSkippedRowReason";

export enum ImportType {
  EMPLOYEE_CALENDAR_CONFIGURATION = "EMPLOYEE_CALENDAR_CONFIGURATION",
  EMPLOYEE_ABSENCE_CONFIGURATION = "EMPLOYEE_ABSENCE_CONFIGURATION",
  ABSENCE_REQUESTS = "ABSENCE_REQUESTS",
}

export enum ExportType {
  CALENDARS = "CALENDARS",
  PATTERNS = "PATTERNS",
}

export enum CalendarNameFilter {
  ALL = "ALL",
  NONE = "NONE",
}

export enum ExportMediaType {
  XLSX = "XLSX",
  CSV = "CSV",
}

export enum BalanceActions {
  ADD = "ADD",
  REMOVE = "REMOVE",
}

export enum ExportImportStatus {
  CREATED = "CREATED",
  SUCCEEDED = "SUCCEEDED",
  FAILED = "FAILED",
}

export enum ErrorType {
  UNPARSEABLE_FILE = "UNPARSEABLE_FILE",
  INVALID_COLUMN_LAYOUT = "INVALID_COLUMN_LAYOUT",
  ROW_ERRORS = "ROW_ERRORS",
}

export enum EMPLOYEE_PRESENCE {
  PRESENT = "PRESENT",
  ABSENT = "ABSENT",
}

export enum PRESENTATION_TYPE {
  DAYS_AND_HOURS = "DAYS_AND_HOURS", // for Absence Types with `billInHours` false (hourly absence request not possible)
  DAYS_OR_HOURS = "DAYS_OR_HOURS", // for Absence Types with `billInHours` true (hourly absence request possible) & no absence request in current billing cycle
  DAYS = "DAYS", // for Absence Types with `billInHours` true (hourly absence request possible) & at least one daily absence request in current billing cycle
  HOURS = "HOURS", // for Absence Types with `billInHours` true (hourly absence request possible) & at least one hourly absence request in current billing cycle
}
export interface IEmployeeRecord extends IEmployeeDetails {
  active: boolean;
  calendar: IEmployeeCalendar | null;
}

export interface IEmployeeRecordCalendars extends IEmployeeDetails {
  content: IEmployeeDetails &
    {
      calendars: IEmployeeCalendar[];
    }[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
}

export interface IEmployeeCalendar {
  calendarId: string;
  name: string;
  year: string;
  isClearable: boolean;
}

export interface IEmployeeRecords {
  content: IEmployeeRecord[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
}

export interface IPatternAssignment {
  absenceTypeId: string;
  patternId: string;
}
export interface IEmployeePatternAssignments {
  patternAssignments: IPatternAssignment[];
}

export interface IEmployeeAssignments {
  calendars: IEmployeeCalendar[];
  patterns: IPatternAssignment[];
}

export interface IEmployeeAssignmentsDTO {
  calendars: {
    [key: string]: string | null;
  };
  patterns: {
    [key: string]: string | null;
  };
}

export interface IOption {
  id: string;
  label: string;
}

export interface IEmployeeBalanceStatus {
  days: number;
  hours: number;
}

export interface IEmployeeBalance {
  confirmedPastUsedTimeAmount: IEmployeeBalanceStatus | null;
  grantedTimeAmount: IEmployeeBalanceStatus | null;
  reservedTimeAmount: IEmployeeBalanceStatus | null;
  overdueAvailableTimeAmount: IEmployeeBalanceStatus | null;
  totalAvailableTimeAmount: IEmployeeBalanceStatus | null;
  presentationType: PRESENTATION_TYPE;
}

export interface IAbsenceTypeInfo {
  absenceGroupId: string;
  active: boolean;
  description?: string;
  id: string;
  isMain: boolean;
  name: string;
  parentAbsenceTypeId: string | null;
  presenceType: PresenceType;
}

export interface IBalanceDetails {
  absenceTypeInfo: IAbsenceTypeInfo;
  status: IEmployeeBalance;
}

export interface IEmployeeBalances {
  absenceGroupInfos: IAbsenceGroup[];
  balanceDetails: IBalanceDetails[];
}

export interface IAbsenceTypeBalances {
  balanceStatuses: {
    billingCycle: string;
    overdueAvailableTimeAmount: IEmployeeBalanceStatus;
    totalAvailableTimeAmount: IEmployeeBalanceStatus;
    presentationType: PRESENTATION_TYPE;
  }[];
}

export interface IAbsenceType {
  id: string;
  name: string;
  description?: string;
  color: string;
  presenceType: PresenceType;
  status: IEmployeeBalance;
  absenceTypes?: IAbsenceType[];
  active: boolean;
  parentName?: string;
}

export interface IExportDTO {
  exportMediaType: ExportMediaType;
  exportType: ExportType;
  companyId: string;
  isActive: boolean | null;
  years?: string[];
}

export interface IExportData {
  exportId: string;
  status: ExportImportStatus;
  companyId: string;
  exportType: ExportType;
  location: string;
}

export type ImportErrorCode = ErrorType | string;

export interface IImportErrorDetails {
  type?: string;
  duplicatedWithRows?: number | number[];
  duplicatedWithRowsCount?: number;
}

export interface IImportRowError {
  columns?: { name: string }[];
  errorCode: ImportErrorCode;
  errorDetails?: IImportErrorDetails | null;
}

export type IImportRowErrors = Record<string, IImportRowError[]>;

// Error that recurs across a large share of the file's rows (`errorDetails` is always absent)
export interface IImportMainError {
  error: IImportRowError;
  rowsCount: number;
}

export interface IImportRequester {
  type: string;
  id: string;
  metadata: Record<string, unknown>;
}

export interface IImportErrors {
  errors: ImportErrorCode | ImportErrorCode[];
  mainErrors?: IImportMainError[];
  rowErrors: IImportRowErrors;
  rowsWithErrorsCount: number;
  rowsWithErrorsLimit: number;
}

export interface ISkippedRow {
  number: number;
  reason: SkippedRowReason;
}

export interface IImportData {
  importId: string;
  companyId: string;
  requester: IImportRequester;
  importType: ImportType;
  status: ExportImportStatus;
  errors: ImportErrorCode | ImportErrorCode[];
  mainErrors?: IImportMainError[];
  rowErrors: IImportRowErrors;
  rowsWithErrorsCount: number;
  rowsWithErrorsLimit: number;
  skippedRows: ISkippedRow[];
}
